import React, { useState, useEffect } from "react";
import { Stepper, Step, StepLabel, Box, Typography, Button } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StepHighSchool from "./StepHighSchool";
import StepHigherSecondary from "./StepHigherSecondary";
import StepDiploma from "./StepDiploma";
import StepUndergraduate from "./StepUndergraduate";
import StepPostgraduate from "./StepPostgraduate";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getEditRequestStatus,
} from "../../../services/candidate/api";

const steps = [
  "High School",
  "Higher Secondary",
  "Diploma",
  "Undergraduate",
  "Postgraduate",
];
const initialDraft = {
  highSchool: {},
  higherSecondary: {},
  diploma: {},
  undergraduate: {},
  postgraduate: {}
};

const EducationStepper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(location.state?.activeStep ?? 0);

  const [educationDraft, setEducationDraft] = useState(() => {
    const saved = localStorage.getItem("educationDraft");
    return saved ? JSON.parse(saved) : initialDraft;
  });

  useEffect(() => {
    localStorage.setItem("educationDraft", JSON.stringify(educationDraft));
  }, [educationDraft]);

  const EDIT_ORDER = [
  "PERSONAL_DETAILS",
  "ADDRESS_DETAILS",
  "EDUCATION_DETAILS",
  "EMPLOYMENT_DETAILS",
];

  const handleEducationContinue = async () => {
    try {
      const statusResponse = await getEditRequestStatus();

      const requests = Array.isArray(statusResponse)
        ? statusResponse
        : [];

      const getLatestRequestByType = (requestType) => {
        return requests
          .filter((request) => request.requestType === requestType)
          .sort((a, b) => {
            const dateA = a.createdAt
              ? new Date(a.createdAt).getTime()
              : 0;

            const dateB = b.createdAt
              ? new Date(b.createdAt).getTime()
              : 0;

            if (dateB !== dateA) {
              return dateB - dateA;
            }

            return Number(b.id || 0) - Number(a.id || 0);
          })[0];
      };

      const educationRequest = getLatestRequestByType("EDUCATION_DETAILS");

      if (educationRequest?.status === "APPROVED") {
        const currentIndex = EDIT_ORDER.indexOf("EDUCATION_DETAILS");

        const nextApprovedRequest = EDIT_ORDER
          .slice(currentIndex + 1)
          .find((requestType) => {
            const latestRequest = getLatestRequestByType(requestType);
            return latestRequest?.status === "APPROVED";
          });

        if (nextApprovedRequest === "EMPLOYMENT_DETAILS") {
          navigate("/candidate/employment-info");
          return;
        }
        navigate("/candidate/declaration");
        return;
      }
      navigate("/candidate/employment-info");

    } catch (error) {
      console.error(
        "Error checking education edit request:",
        error
      );

      navigate("/candidate/employment-info");
    }
  };

  const updateStepData = (step, data) => {
    setEducationDraft(prev => ({
      ...prev,
      [step]: data
    }));
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      navigate("/candidate/address-info");
    }
  };

  const getStepComponent = () => {
    switch (activeStep) {

      case 0:
        return (
          <StepHighSchool
            data={educationDraft.highSchool}
            updateData={(data) => updateStepData("highSchool", data)}
            onNext={() => setActiveStep(1)}
            onBack={handleBack}
          />
        );

      case 1:
        return (
          <StepHigherSecondary
            data={educationDraft.higherSecondary}
            updateData={(data) => updateStepData("higherSecondary", data)}
            onNext={() => setActiveStep(2)}
            onBack={handleBack}
          />
        );

      case 2:
        return (
          <StepDiploma
            data={educationDraft.diploma}
            updateData={(data) => updateStepData("diploma", data)}
            onNext={() => setActiveStep(3)}
            onBack={handleBack}
          />
        );

      case 3:
        return (
          <StepUndergraduate
            data={educationDraft.undergraduate}
            updateData={(data) => updateStepData("undergraduate", data)}
            onNext={() => setActiveStep(4)}
            onBack={handleBack}
          />
        );

      case 4:
        return (
          <StepPostgraduate
            data={educationDraft.postgraduate}
            updateData={(data) =>
              updateStepData("postgraduate", data)
            }
            onNext={() => setActiveStep(5)}
            onBack={handleBack}
          />
        );

      default:
        localStorage.removeItem("educationDraft");

        return (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ mt: 5 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 60, color: "green" }} />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: "bold" }}>
              All education steps completed!
            </Typography>

            <Box display="flex" gap={2} sx={{ mt: 3 }}>
              <Button variant="outlined" onClick={() => navigate("/candidate/address-info")}>
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleEducationContinue}
              >
                Save & Continue
              </Button>
            </Box>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ width: "100%", px: 2 }}>

      {/* Centered Container (Shared width) */}
      <Box
        sx={{
          maxWidth: "896px",
          margin: "0 auto",
        }}
      >

        <Box
          sx={{
            position: "sticky",
            top: 49,
            //  zIndex: 10,
            backgroundColor: "#fff",
            py: 2,
            px: 2,
            borderBottom: "1px solid #eee",
            width: "100%",
          }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              width: "100%",
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ mt: 3 }}>
          {getStepComponent()}
        </Box>

      </Box>
    </Box>
  );
};

export default EducationStepper;